import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { TypographySize } from 'mm-theme-configuration/dist/consts';
import { Link } from '../../../../partials/link/Link';
import { useTheme } from '../../../../../theming/useTheme';
import { BaseUnit } from '../../../../../theming/baseUnitDefinition';
import { getLinkColorsStyle } from './linkStyle.utils';
import { Span } from '../../../../../typography/semanticTags';
import { getStyleBySize } from '../../../../../typography/semanticTags/semanticTags.utils';
import { LinkWithSubMenu } from './LinkWithSubMenu';
import { useSubMenu } from './subMenuReducer';
import { NavigationLink } from '../primaryHeader/PrimaryHeader';
import { AnimationDirection, AnimationDirectionType } from '../menuAnimations.util';
import { reducedHeaderFirstRowHeightFactors } from '../header.utils';

const MAX_NUM_ROWS = 7;

interface SubMenuProps {
  links: NavigationLink[];
  parentElement: HTMLElement;
  activeStateColor: string;
  hoverStateColor: string;
  idleColor: string;
  currentLinkColor: string;
  pagePath: string;
  linkFontSize: TypographySize;
  backgroundColor: string;
  shouldOffsetLeft: boolean;
  menuFoldState: AnimationDirectionType;
  animationDuration: number;
  openLinkIndex: number;
  innerLinksBackgroundColor?: string;
  innerLinkFontSize?: TypographySize;
  linkDropdownIconColor: string;
}

const getStyles = (backgroundColor: string, baseUnit: BaseUnit, offset: number, numMenuRows: number, minWidth: number, shouldOffsetLeft: boolean, animationDuration: number, menuFoldState: AnimationDirectionType) => {
  return StyleSheet.create({
    ul: {
      position: 'absolute',
      left: shouldOffsetLeft ? 0 : `${offset - baseUnit / 2}px`,
      transition: `top ${animationDuration}ms ease 0s`,
      top: menuFoldState === AnimationDirection.UP ? `${baseUnit * 2 * reducedHeaderFirstRowHeightFactors.large}px` : `${baseUnit * 2}px`,
      backgroundColor,
      listStyle: 'none',
      padding: `${baseUnit / 2}px`,
      display: 'grid',
      gridRowGap: `${baseUnit / 2}px`,
      gridColumnGap: `${baseUnit * 4}px`,
      gridAutoFlow: 'column',
      gridTemplateRows: `repeat(${numMenuRows}, 1fr)`,
      minWidth: `${minWidth}px`,
      transform: shouldOffsetLeft ? 'translateX(-100%)' : '',
    },
    li: {
      padding: 0,
      position: 'relative',
    },
    link: {
      whiteSpace: 'nowrap',
      cursor: 'pointer',
    },
  });
};

const getOpenListItemStyle = (isSubMenuOpen: boolean, activeMenuBackgroundColor: string, baseUnit: BaseUnit) => {
  if (!isSubMenuOpen) return null;

  return StyleSheet.create({
    li: {
      position: 'relative',
      ':before': {
        content: isSubMenuOpen ? "''" : '',
        backgroundColor: activeMenuBackgroundColor,
        position: 'absolute',
        top: `-${baseUnit / 2}px`,
        left: `-${baseUnit / 2}px`,
        bottom: `-${baseUnit / 2}px`,
        right: `-${baseUnit / 2}px`,
        zIndex: 0,
      },
    },
  }).li;
};

const getMenuOffset = (parentElement: HTMLElement) => {
  const offset = parentElement.getBoundingClientRect().left;
  return offset;
};

const getMenuMinWidth = (parentElement: HTMLElement) => {
  return parentElement.getBoundingClientRect().width;
};

const getWrapperStyle = (element: HTMLElement, baseUnit: BaseUnit) => {
  return StyleSheet.create({
    subMenuWrapper: {
      position: 'absolute',
      left: `-${baseUnit / 2}px`,
      top: `-${baseUnit * 2.5}px`,
    },
  });
};

export const SubMenu: React.FunctionComponent<SubMenuProps> = props => {
  const { links, parentElement, activeStateColor, hoverStateColor, idleColor, currentLinkColor, linkFontSize, backgroundColor, pagePath, shouldOffsetLeft, animationDuration, menuFoldState, openLinkIndex, innerLinkFontSize, innerLinksBackgroundColor, linkDropdownIconColor } = props;
  const { baseUnit, fontSizesStyles } = useTheme();
  const [subMenuState, dispatch] = useSubMenu();
  const offset = getMenuOffset(parentElement);
  const numMenuRows = Math.min(links.length, MAX_NUM_ROWS);
  const minWidth = getMenuMinWidth(parentElement);
  const styles = getStyles(backgroundColor, baseUnit, offset, numMenuRows, minWidth, shouldOffsetLeft, animationDuration, menuFoldState);
  const fontStyle = getStyleBySize(fontSizesStyles, { fontSizeSmall: linkFontSize, fontSizeMedium: linkFontSize, fontSizeLarge: linkFontSize });
  const isMenuOpen = subMenuState.links && subMenuState.links.length > 0;
  const ulRef = React.useRef<HTMLUListElement>(null);

  const onHoverLink = (element: HTMLElement, index: number) => {
    if (isMenuOpen) return;
    const menuLinks = links![index].children;
    dispatch({
      data: {
        links: menuLinks,
        element,
        index,
        linkFontSize: innerLinkFontSize || linkFontSize,
        backgroundColor: innerLinksBackgroundColor || backgroundColor,
      },
    });
  };

  const onLeaveLink = () => {
    dispatch({
      data: {
        links: null,
        element: null,
        index: -1,
        linkFontSize: innerLinkFontSize || linkFontSize,
        backgroundColor: innerLinksBackgroundColor || backgroundColor,
      },
    });
  };

  return (
    <React.Fragment>
      <ul className={css(styles.ul)} ref={ulRef}>
        {
          links.map((navigationLink, index) => {
            const isLinkActive = pagePath === navigationLink.link.href;
            const isLinkWithSubMenu = navigationLink.children !== null && navigationLink.children.length > 0;
            return (
              <li key={index} className={css(getOpenListItemStyle(subMenuState.index === index, innerLinksBackgroundColor || backgroundColor, baseUnit))} onMouseEnter={e => (isLinkWithSubMenu && onHoverLink(e.target as HTMLElement, index))} onMouseLeave={onLeaveLink}>
                {
                  isLinkWithSubMenu ? (
                    <React.Fragment>
                      {
                        isMenuOpen && subMenuState.index === index && (
                          <div className={css(getWrapperStyle(subMenuState.element!, baseUnit).subMenuWrapper)}>
                            <SubMenu
                              activeStateColor={activeStateColor}
                              hoverStateColor={hoverStateColor}
                              idleColor={idleColor}
                              backgroundColor={innerLinksBackgroundColor || backgroundColor}
                              linkFontSize={innerLinkFontSize || linkFontSize}
                              pagePath={pagePath}
                              links={subMenuState.links!}
                              parentElement={subMenuState.element!}
                              menuFoldState={menuFoldState}
                              animationDuration={animationDuration}
                              shouldOffsetLeft
                              currentLinkColor={currentLinkColor}
                              openLinkIndex={subMenuState.index}
                              linkDropdownIconColor={linkDropdownIconColor}
                            />
                          </div>
                        )
                      }
                      <LinkWithSubMenu
                        title={navigationLink.link.text}
                        hoverStateColor={hoverStateColor}
                        idleColor={openLinkIndex === index ? hoverStateColor : idleColor}
                        index={index}
                        linkDropdownIconColor={linkDropdownIconColor}
                        linkFontSizeLarge={linkFontSize}
                        linkFontSizeMedium={linkFontSize}
                        linkFontSizeSmall={linkFontSize}
                        isOpen={subMenuState.index === index}
                        activeStateColor={activeStateColor}
                        currentLinkColor={currentLinkColor}
                      />
                    </React.Fragment>
                  )
                    : (
                      <Link href={navigationLink.link.href} className={css(styles.link)}>
                        <Span className={css(fontStyle, getLinkColorsStyle(activeStateColor, hoverStateColor, idleColor, currentLinkColor, isLinkActive).link)}>{navigationLink.link.text}</Span>
                      </Link>

                    )
                }
              </li>
            );
          })
        }
      </ul>
    </React.Fragment>
  );
};
